import { useCallback, useEffect, useState } from "react";
import {
  DISCOVER_SOURCES,
  fetchDiscover,
  type DiscoverMedia,
} from "@/features/discover/api";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { MediaCard, MediaCardSkeleton } from "@/shared/ui/MediaCard";
import { PageHeader } from "@/shared/ui/PageHeader";

/** 发现：豆瓣/TMDB/Bangumi 精选榜单 */
export function DiscoverPage() {
  const [sourceId, setSourceId] = useState(DISCOVER_SOURCES[0].id);
  const [items, setItems] = useState<DiscoverMedia[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const source = DISCOVER_SOURCES.find((s) => s.id === sourceId) ?? DISCOVER_SOURCES[0];

  const load = useCallback(
    async (pageNum: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }
      try {
        const list = await fetchDiscover(source.path, pageNum, 30);
        const next = Array.isArray(list) ? list : [];
        setItems((prev) => (append ? [...prev, ...next] : next));
        setHasMore(next.length >= 30);
        setPage(pageNum);
      } catch (e) {
        setError(e instanceof Error ? e.message : "加载失败");
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [source.path],
  );

  useEffect(() => {
    void load(1, false);
  }, [load]);

  return (
    <div>
      <PageHeader
        title="发现"
        description={source.label}
        actions={
          <Button
            variant="ghost"
            onClick={() => void load(1, false)}
            disabled={loading}
          >
            刷新
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {DISCOVER_SOURCES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setSourceId(s.id);
              setPage(1);
            }}
            className={cn(
              "h-8 rounded-[var(--radius-sm)] border px-3 text-[13px] transition-colors",
              sourceId === s.id
                ? "border-brand/40 bg-brand-muted text-brand"
                : "border-border text-fg-muted hover:bg-bg-hover",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? <MediaCardSkeleton /> : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <EmptyState title="暂无内容" description="换一个榜单试试" />
      ) : null}

      {!loading && items.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
            {items.map((item, i) => (
              <MediaCard
                key={`${item.tmdb_id ?? item.douban_id ?? item.bangumi_id ?? item.title}-${i}`}
                item={item}
              />
            ))}
          </div>
          {hasMore ? (
            <div className="mt-5 flex justify-center">
              <Button
                variant="ghost"
                disabled={loadingMore}
                onClick={() => void load(page + 1, true)}
              >
                {loadingMore ? "加载中…" : "加载更多"}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
